import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { TASKS } from "@/data/tasks";
import { Corners, PageHead, Reveal, StatusDot, Tag } from "@/components/ui-kit";

const FILTERS = ["ALL", "OPEN", "PROCESSING", "COMPLETED"];

const TasksPage = () => {
  const [filter, setFilter] = useState("ALL");
  const [active, setActive] = useState(null);
  const list = TASKS.filter((t) => filter === "ALL" || t.status === filter);
  return (
    <div data-testid="tasks-page" className="pb-24">
      <PageHead
        eyebrow="MODULE 04 // TASK MARKET"
        title="AUTONOMOUS TASK MARKET"
        subtitle="Machines need work. Agents need machines. Tasks are posted, matched, executed and settled — autonomously."
        note="SIMULATED MARKET — FICTIONAL DEMO VALUES"
      />
      <div className="px-5 sm:px-8 max-w-7xl mx-auto">
        <div className="flex flex-wrap gap-2 mb-6">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`task-filter-${f.toLowerCase()}`}
              data-cursor="SCAN"
              onClick={() => setFilter(f)}
              aria-pressed={filter === f}
              className={`px-3 py-1.5 font-mono text-[10px] tracking-[0.25em] border transition-colors duration-200 ${
                filter === f ? "border-arc-electric text-arc-cyan bg-arc-blue/10" : "border-arc-silver/20 text-arc-silver/50 hover:border-arc-electric/50"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((t, i) => (
            <Reveal key={t.id} delay={i * 0.05}>
              <button
                data-testid={`task-card-${t.id.replace(/[#\s]/g, "-").toLowerCase()}`}
                data-cursor="SCAN"
                onClick={() => setActive(t)}
                className="hud-panel relative w-full text-left p-5 group hover:border-arc-electric/60 transition-colors duration-300"
                aria-haspopup="dialog"
              >
                <Corners className="border-arc-electric/0 group-hover:border-arc-electric/70 transition-colors duration-300" />
                <div className="flex items-center justify-between mb-3">
                  <span className="font-mono text-[11px] text-arc-silver/50 tracking-wider">{t.id}</span>
                  <span className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] text-arc-silver/60"><StatusDot status={t.status} />{t.status}</span>
                </div>
                <h3 className="font-display text-lg font-bold tracking-wide text-arc-white">{t.title}</h3>
                <div className="mt-4 flex items-end justify-between">
                  <div>
                    <div className="font-mono text-[9px] tracking-[0.3em] text-arc-silver/40 mb-1">REWARD</div>
                    <div className="font-mono text-xl text-arc-cyan">{t.reward}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-[9px] tracking-[0.3em] text-arc-silver/40 mb-1">REQUIRED</div>
                    <div className="flex flex-wrap gap-1 justify-end">{t.required.map((r) => <Tag key={r}>{r}</Tag>)}</div>
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-arc-blue/10 flex justify-between font-mono text-[9px] tracking-[0.2em] text-arc-silver/35">
                  <span>{t.sector}</span><span className="group-hover:text-arc-cyan/70 transition-colors">&gt; DETAILS_</span>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <>
            <motion.div className="fixed inset-0 z-[65] bg-arc-black/70 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setActive(null)} />
            <motion.aside
              data-testid="task-drawer"
              className="fixed top-0 right-0 bottom-0 z-[70] w-full sm:w-[440px] hud-panel border-l border-arc-electric/30 overflow-y-auto p-6 sm:p-8"
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.28, ease: "easeOut" }}
              role="dialog" aria-modal="true" aria-label={`${active.id} details`}
            >
              <button onClick={() => setActive(null)} data-testid="task-drawer-close" aria-label="Close task details" className="absolute top-5 right-5 text-arc-silver/50 hover:text-arc-cyan"><X size={18} /></button>
              <div className="font-mono text-[11px] text-arc-silver/50 tracking-wider mb-1">{active.id}</div>
              <h3 className="font-display text-2xl font-bold text-arc-white tracking-wide">{active.title}</h3>
              <div className="mt-3 flex items-center gap-3">
                <Tag tone={active.status === "OPEN" ? "blue" : active.status === "COMPLETED" ? "green" : "amber"}>{active.status}</Tag>
                <span className="font-mono text-lg text-arc-cyan">{active.reward}</span>
              </div>
              <p className="mt-5 text-sm text-arc-silver/70 leading-relaxed">{active.description}</p>
              <div className="mt-6 space-y-2.5 font-mono text-xs">
                <div className="flex justify-between"><span className="text-arc-silver/40">POSTED BY</span><span className="text-arc-silver">{active.postedBy}</span></div>
                <div className="flex justify-between"><span className="text-arc-silver/40">SECTOR</span><span className="text-arc-silver">{active.sector}</span></div>
                <div className="flex justify-between"><span className="text-arc-silver/40">DEADLINE</span><span className="text-arc-silver">{active.deadline}</span></div>
                <div className="flex justify-between"><span className="text-arc-silver/40">SETTLEMENT</span><span className="text-arc-cyan">ARC · USDC</span></div>
              </div>
              <div className="font-mono text-[10px] tracking-[0.3em] text-arc-silver/40 mt-6 mb-2">EXECUTION PIPELINE</div>
              <div className="space-y-1.5">
                {active.steps.map((s, i) => (
                  <div key={s} className="flex items-center gap-3 border border-arc-blue/10 px-3 py-2 font-mono text-[11px] text-arc-silver/70">
                    <span className="text-arc-electric">{String(i + 1).padStart(2, "0")}</span>{s}
                  </div>
                ))}
              </div>
              <div className="mt-6 font-mono text-[9px] tracking-[0.2em] text-arc-silver/30">FICTIONAL TASK — MARKET SIMULATION ONLY</div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TasksPage;
